import { Typography } from "@suraasa/placebo-ui"
import { AuthManager } from "features/Authentication/AuthManager"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useCallback, useEffect, useMemo, useState } from "react"
import Joyride, { Step } from "react-joyride"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import { sleep } from "utils/helpers"
import { preloadImages } from "utils/preloadImages"
import { isShadow } from "utils/shadow"

import { SpnyTourEnum, useSuraasaPlusNewYear } from "../Context"
import OnboardingStep from "../OnboardingStep"
import { tourManager } from "../TourManager"
import AiToolsImg from "./assets/ai-tools.webp"
import CpatSkillImg from "./assets/cpat-skill-img.webp"
import ImpactCourseImg from "./assets/impact-course.webp"
import MasterclassesImg from "./assets/masterclasses.webp"
import PremiumJobImg from "./assets/premium-jobs-img.webp"
import { DASHBOARD_ONBOARDING_TARGETS } from "./constants"
import GetStarted from "./GetStarted"

const DashboardTour = () => {
  const { isSPNYUser } = useSuraasaPlusContext()
  const {
    isPendingTour,
    setIsSpnyPlatformIntroCompleted,
    isSpnyPlatformIntroCompleted,
    setOpenOnboarding,
    spnyPlatformIntro,
  } = useSuraasaPlusNewYear()

  const [step, setStep] = useState<"intro" | "joyride" | null>(null)

  useEffect(() => {
    if (!isSPNYUser) return
    const auth = AuthManager.getPlatformAuth()
    if (!auth || isShadow()) return
    const isCompleted = spnyPlatformIntro === SpnyTourEnum.completed

    ;(async () => {
      await preloadImages([
        AiToolsImg,
        CpatSkillImg,
        ImpactCourseImg,
        MasterclassesImg,
        PremiumJobImg,
      ])
      await sleep(250)
    })()
    setIsSpnyPlatformIntroCompleted(isCompleted)
    if (!isCompleted) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStep("intro")
      return
    }
    if (isCompleted && isPendingTour) {
      setStep(null)
      return
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingTour, isSPNYUser])

  const onComplete = useCallback(() => {
    tourManager.completed(BROWSER_STORAGE_KEYS.spnyPlatformIntro)
    setStep(null)
    setIsSpnyPlatformIntroCompleted(true)
    setOpenOnboarding("dashboard-personal-onboarding")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleJoyrideCallback = useCallback(
    data => {
      const { status, lifecycle } = data

      const tourCompletedStatuses = ["finished"]
      if (tourCompletedStatuses.includes(status) && lifecycle === "complete") {
        onComplete()
      }
    },
    [onComplete]
  )

  const steps: Step[] = useMemo(
    () => [
      {
        target: `.${DASHBOARD_ONBOARDING_TARGETS.learning}`,
        content: (
          <div className="-mt-1.5">
            <div className="mb-2.5 flex h-[82px] items-end">
              <img
                src={ImpactCourseImg}
                alt="impact-course-img"
                className="h-[71px] object-contain"
              />
            </div>
            <Typography variant="title3" className="mb-1 text-start">
              Your IMPACT Course is ready.
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-3 text-start text-onSurface-600"
            >
              Find your quick-start course here and begin classroom success
              today.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        // placement: "bottom",
        data: {
          route: routes.masterclasses,
        },
      },
      {
        target: `.${DASHBOARD_ONBOARDING_TARGETS.masterclasses}`,
        content: (
          <div className="-mt-1.5">
            <div className="mb-2.5 flex h-[82px] items-center">
              <img
                src={MasterclassesImg}
                alt="masterclasses-img"
                className="size-[52px] object-contain"
              />
            </div>
            <Typography variant="title3" className="mb-1 text-start">
              Expert Masterclasses
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-3 text-start text-onSurface-600"
            >
              Explore 60+ expert masterclass recordings and tune in for the
              upcoming ones.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        // placement: "bottom",
        data: {
          route: routes.aiTools.home,
        },
      },
      {
        target: `.${DASHBOARD_ONBOARDING_TARGETS.aiTools}`,
        content: (
          <div className="-mt-1.5">
            <div className="mb-2.5 flex h-[82px] items-center">
              <img
                src={AiToolsImg}
                alt="ai-tools-img"
                className="h-[53px] object-contain"
              />
            </div>
            <Typography variant="title3" className="mb-1 text-start">
              Advanced AI Tools
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-3 text-start text-onSurface-600"
            >
              Access 11 AI teaching tools to create rubrics, lesson plans,
              assessments and more, in seconds.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        // placement: "bottom",
        data: {
          route: routes.cpat.dashboard,
        },
      },
      {
        target: `.${DASHBOARD_ONBOARDING_TARGETS.cpatAssessment}`,
        content: (
          <div className="-mt-1.5">
            <div className="mb-2.5 flex h-[82px]">
              <img
                src={CpatSkillImg}
                alt="cpat-skill-img"
                className="h-full object-contain"
              />
            </div>
            <Typography variant="title3" className="mb-1 text-start">
              CPAT Skill Analysis
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-3 text-start text-onSurface-600"
            >
              Begin your skill assessment to understand your exact strengths for
              targeted growth.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        // placement: "bottom",
        data: {
          route: routes.home,
        },
      },
      {
        target: `.${DASHBOARD_ONBOARDING_TARGETS.jobs}`,
        content: (
          <div className="-mt-1.5">
            <div className="mb-2.5 flex h-[82px]">
              <img
                src={PremiumJobImg}
                alt="premium-job-img"
                className="h-full object-contain"
              />
            </div>
            <Typography variant="title3" className="mb-1 text-start">
              Premium Teaching Jobs
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-3 text-start text-onSurface-600"
            >
              Discover and apply to unlimited premium teaching jobs at top
              schools around the globe.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        // placement: "bottom",
      },
    ],
    []
  )

  return (
    <>
      <div>
        <GetStarted
          open={step === "intro"}
          handleContinue={() => {
            // trackingService.trackEvent(TrackingEvents.profileTourStarted)
            tourManager.initiated(BROWSER_STORAGE_KEYS.spnyPlatformIntro)
            setStep("joyride")
          }}
          handleClose={() => {
            setStep(null)
            setOpenOnboarding(null)
          }}
          isPendingTour={isPendingTour && isSpnyPlatformIntroCompleted}
        />

        <Joyride
          steps={steps}
          run={step === "joyride"}
          tooltipComponent={OnboardingStep}
          continuous
          disableOverlayClose
          scrollOffset={200}
          disableCloseOnEsc
          disableScrolling
          hideBackButton
          hideCloseButton
          callback={handleJoyrideCallback}
        />
      </div>
    </>
  )
}

export default DashboardTour
